export async function getFileFromUrl(url: string, name = '', defaultType = 'image/jpeg'): Promise<File | undefined> {
  try {
    const fileName = name ?? url.split('/')?.pop() ?? 'imagem';

    const response = await fetch(url);
    const data = await response.blob();

    return new File([data], fileName, {
      type: data.type || defaultType,
    });
  } catch (err) {
    console.log(err);
    return undefined;
  }
}

export function renameFile(originalFile: File, newName: string) {
  return new File([originalFile], newName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
  });
}

const COMPRESSIBLE_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

interface CompressImageOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}

/**
 * Reduz uma imagem no navegador (canvas, preservando o tipo original) antes do upload, evitando 413.
 * O tipo NUNCA é convertido: converter um PNG transparente para JPEG enegreceria o fundo
 * (JPEG não tem canal alpha). A redução vem do resize e, em JPEG/WebP, também do quality.
 * Arquivos que não são imagens (ex.: vídeos .mp4) são retornados sem alteração.
 */
export async function compressImage(
  file: File,
  { maxWidth = 1920, maxHeight = 1920, quality = 0.8 }: CompressImageOptions = {},
): Promise<File> {
  if (!COMPRESSIBLE_IMAGE_TYPES.includes(file.type)) {
    return file;
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

  const scale = Math.min(1, maxWidth / image.naturalWidth, maxHeight / image.naturalHeight);
  const targetWidth = Math.round(image.naturalWidth * scale);
  const targetHeight = Math.round(image.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return file;

  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

  // Mantém o tipo original (canvas usa 'image/jpeg' como MIME canônico, não 'image/jpg').
  const outputType = file.type === 'image/jpg' ? 'image/jpeg' : file.type;

  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, outputType, quality));
  if (!blob || blob.size >= file.size) return file;

  // Preserva nome/extensão original — o tipo não muda.
  return new File([blob], file.name, { type: outputType, lastModified: Date.now() });
}

/**
 * Orçamento de imagem de opção de enquete. O app renderiza essa imagem em 40x40
 * pontos (120px numa tela 3x), então 512px é folga larga. Com 25 opções no teto,
 * o upload inteiro fica em ~7,5MB — bem abaixo do que o backend aceita.
 */
export const POLL_OPTION_IMAGE_BUDGET = { maxDimension: 512, maxBytes: 300 * 1024 };

function hasAlphaInUse(ctx: CanvasRenderingContext2D, width: number, height: number): boolean {
  const { data } = ctx.getImageData(0, 0, width, height);

  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }

  return false;
}

async function drawToBlob(
  image: HTMLImageElement,
  scale: number,
  type: string,
  quality: number,
): Promise<{ blob: Blob; hasAlpha: boolean } | undefined> {
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;

  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, 0, 0, width, height);

  const hasAlpha = type === 'image/png' && hasAlphaInUse(ctx, width, height);

  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, type, quality));
  if (!blob) return undefined;

  return { blob, hasAlpha };
}

/**
 * Comprime até caber num orçamento de bytes, em vez de fazer um passe único e torcer.
 *
 * Existe porque `compressImage` não dá garantia de tamanho em PNG: o canvas ignora
 * o parâmetro `quality` quando o tipo é PNG, então um PNG de foto continua pesando
 * megabytes depois do resize. Aqui, quando o PNG não usa transparência de fato, ele
 * é reencodado como JPEG (sem risco de fundo preto, que é o motivo de `compressImage`
 * nunca converter). Com alpha em uso, resta apertar a dimensão.
 */
export async function compressImageToBudget(
  file: File,
  { maxDimension, maxBytes } = POLL_OPTION_IMAGE_BUDGET,
): Promise<File> {
  if (!COMPRESSIBLE_IMAGE_TYPES.includes(file.type)) {
    return file;
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

  const inputType = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
  const baseScale = Math.min(1, maxDimension / image.naturalWidth, maxDimension / image.naturalHeight);

  // Primeiro passe no tipo original: preserva PNG quando ele já couber no orçamento.
  const first = await drawToBlob(image, baseScale, inputType, 0.85);
  if (!first) return file;
  if (first.blob.size <= maxBytes) {
    return new File([first.blob], file.name, { type: inputType, lastModified: file.lastModified });
  }

  // Não coube. PNG sem transparência vira JPEG; com transparência, só dá para encolher.
  const outputType = inputType === 'image/png' && !first.hasAlpha ? 'image/jpeg' : inputType;
  const name =
    outputType === inputType ? file.name : file.name.replace(/\.[^.]+$/, '') + '.jpg';

  let best = first.blob;
  for (const [scaleFactor, quality] of [
    [1, 0.8],
    [0.75, 0.7],
    [0.5, 0.6],
    [0.35, 0.5],
  ] as const) {
    const attempt = await drawToBlob(image, baseScale * scaleFactor, outputType, quality);
    if (!attempt) break;

    best = attempt.blob;
    if (attempt.blob.size <= maxBytes) break;
  }

  // Se nem assim coubesse, `best` ainda é muito menor que o original — vale enviar.
  if (best.size >= file.size) return file;

  return new File([best], name, { type: outputType, lastModified: file.lastModified });
}